// Description: Cliente/helper reutilizável da dapi (toggle por rota + fallback).
// Fase 4 (issue #114, épico #113): extrai o padrão dapi/toggle/fallback que
// estava hardcoded na listagem de campanhas (#97) para um módulo central,
// pronto para ser reutilizado pelas demais rotas (#115/#116/#117).

import axios from 'axios';

// ---------------------------------------------------------------------------
// Convenção de flags = POR ROTA (granular, para o piloto).
//
// Cada rota tem sua própria flag em localStorage, no namespace "ds:":
//   - ds:campaigns
//   - ds:templates
//   - ds:profile
//   - ds:sheets
//
// Valor de cada flag ∈ { ausente | "lambda" (DEFAULT) , "dapi" }:
//   - ausente ou "lambda" → usa o caminho atual (Lambda). É o default de
//     produção.
//   - "dapi" → tenta a dapi e, em QUALQUER falha, cai automaticamente no
//     Lambda (fallback).
//
// Não há master flag por enquanto: o piloto liga/desliga cada rota de forma
// independente. As flags ainda não "fiadas" (templates/profile/sheets) já
// existem na convenção para quando os respectivos services forem migrados.
// ---------------------------------------------------------------------------
export const DAPI_FLAG_PREFIX = 'ds:';

export const DAPI_FLAGS = {
  campaigns: 'ds:campaigns',
  templates: 'ds:templates',
  profile: 'ds:profile',
  sheets: 'ds:sheets',
};

// Default hardcoded de propósito: NÃO depender de nova env var no CI
// (já houve incidente de env faltando no deploy.yml). Se a var existir,
// ela tem prioridade; senão usamos o host de produção da dapi.
export const DAPI_URL = process.env.REACT_APP_DAPI_URL || 'https://dapi.123zap.com.br';

// Timeout curto para a dapi: se ela demorar, caímos no fallback Lambda
// rápido em vez de travar a tela.
export const DAPI_TIMEOUT_MS = 5000;

/**
 * Indica se a dapi está habilitada para uma rota (flagKey).
 * Lê o localStorage com try/catch (contextos sem localStorage → false).
 */
export function isDapiEnabled(flagKey) {
  try {
    return localStorage.getItem(flagKey) === 'dapi';
  } catch {
    // Em contextos sem localStorage (SSR/teste) tratamos como default (Lambda).
    return false;
  }
}

/**
 * GET na dapi usando o ID token no Bearer.
 * Retorna response.data (mesmo shape do caminho Lambda).
 */
export async function dapiGet(path, idToken) {
  const response = await axios.get(`${DAPI_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    timeout: DAPI_TIMEOUT_MS,
  });

  return response.data;
}

/**
 * Executa dapiFn quando a rota está em modo "dapi", com fallback automático
 * para lambdaFn em qualquer falha. Quando a rota está em modo Lambda (default),
 * executa direto lambdaFn.
 *
 * @param {string} flagKey  chave da flag (ex.: DAPI_FLAGS.campaigns)
 * @param {() => Promise<any>} dapiFn   chamada via dapi
 * @param {() => Promise<any>} lambdaFn chamada via Lambda (default e fallback)
 */
export async function callWithDapiFallback(flagKey, dapiFn, lambdaFn) {
  if (isDapiEnabled(flagKey)) {
    try {
      return await dapiFn();
    } catch (dapiError) {
      // Qualquer falha da dapi (timeout, rede, HTTP 5xx, 429, e até erro de
      // auth) NÃO deve degradar a tela: refazemos a chamada no modo Lambda e
      // retornamos esse resultado.
      console.warn(
        `dapi falhou (${flagKey}); usando fallback Lambda:`,
        dapiError?.message || dapiError
      );

      return await lambdaFn();
    }
  }

  // Modo lambda (DEFAULT): caminho atual inalterado.
  return await lambdaFn();
}

/**
 * Remove TODAS as flags "ds:*" do localStorage.
 * Usado no logout: as flags são preferências internas/experimentais e não
 * devem vazar entre sessões/usuários na mesma máquina.
 */
export function clearAllDapiFlags() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DAPI_FLAG_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignora ambientes sem localStorage
  }
}
